import { useAuth } from 'context/AuthContext';
import UserDashboard from './UserDashboard';
import AssemblyAdminDashboard from './AssemblyAdminDashboard';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { propertyService } from 'services/api';
import styles from './Dashboard.module.css';

// ─── Colores ──────────────────────────────────────────────────────────────────
const STATUS_COLOR = { active: '#12b76a', trial: '#f79009', inactive: '#f04438' };
const STATUS_LABEL = { active: 'Activa', trial: 'Trial', inactive: 'Inactiva' };
const STATUS_BG    = { active: '#12b76a22', trial: '#f7900922', inactive: '#f0443822' };

// ─── Mini bar chart (SVG puro, sin librerías) ─────────────────────────────────
function BarChart({ data, colorFn }) {
  const max = Math.max(...data.map(d => d.value), 1);
  const W = 280, H = 100, barW = Math.floor(W / data.length) - 6;
  // Etiquetas inclinadas 45° para que no se pisen; PAD_L deja sitio a la primera
  // (se extiende hacia la izquierda) y el pie mayor a las más largas.
  const PAD_L = 40, FOOT = 56;
  return (
    <svg width="100%" viewBox={`${-PAD_L} 0 ${W + PAD_L} ${H + FOOT}`} className={styles.graficoBarras}
      role="img" aria-label="Gráfico de barras de unidades por propiedad">
      {data.map((d, i) => {
        const bh = Math.max(4, (d.value / max) * H);
        const x  = i * (W / data.length) + 3;
        const y  = H - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx={4}
              fill={colorFn ? colorFn(d) : 'var(--accent)'} opacity={0.85} />
            <text x={x + barW / 2} y={H + 10} textAnchor="end"
              transform={`rotate(-45 ${x + barW / 2} ${H + 10})`}
              fontSize={9} fill="var(--text-muted)"
              className={styles.textoSvg}>
              <title>{`${d.label}: ${d.value}`}</title>
              {d.label.length > 14 ? d.label.slice(0, 13) + '…' : d.label}
            </text>
            {d.value > 0 && (
              <text x={x + barW / 2} y={y - 4} textAnchor="middle"
                fontSize={9} fill="var(--text)" fontWeight={600}>
                {d.value}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Donut chart (SVG puro) ───────────────────────────────────────────────────
function DonutChart({ segments }) {
  const total = segments.reduce((s, d) => s + d.value, 0) || 1;
  const R = 48, cx = 60, cy = 60, stroke = 10;
  let offset = 0;
  const circ = 2 * Math.PI * R;
  return (
    <svg width={120} height={120}>
      {segments.map((seg, i) => {
        const pct  = seg.value / total;
        const dash = pct * circ;
        const gap  = circ - dash;
        const el   = (
          <circle key={i} cx={cx} cy={cy} r={R}
            fill="none" stroke={seg.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            className={styles.segmentoDona}
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cy + 5} textAnchor="middle" fontSize={13} fontWeight={700} fill="var(--text)">
        {total}
      </text>
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize={8} fill="var(--text-muted)">
        total
      </text>
    </svg>
  );
}

// ─── Dashboard Super Admin ────────────────────────────────────────────────────
function SuperAdminDashboard() {
  // Resumen exacto calculado en el servidor (conteos por estado real, vencen en 30 días, las 20 más
  // próximas, las 10 más recientes y el Top 10 por unidades). Antes se pedían las 100 propiedades más
  // nuevas y los números salían de ellas. El detalle completo está en Membresías.
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    propertyService.dashboardResumen()
      .then(r => { setResumen(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const props        = resumen?.recientes || [];
  const total        = resumen?.total || 0;
  const byStatus     = resumen?.por_estado || { active: 0, trial: 0, inactive: 0 };
  const expiringSoon = resumen?.proximas || [];
  const nVencen      = resumen?.vencen_30 || 0;
  const topByUnits   = (resumen?.top_unidades || []).map(p => ({ ...p, unitCount: p.unidades, realStatus: p.estado_real }));

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={styles.columna}>

      {/* KPI cards */}
      <div className={styles.kpis}>
        {[
          { label: 'Total propiedades', value: total,                icon: 'fa-building',      color: '#6c63ff' },
          { label: 'Activas',           value: byStatus.active,     icon: 'fa-circle-check',  color: '#12b76a' },
          { label: 'En trial',          value: byStatus.trial,      icon: 'fa-clock',         color: '#f79009' },
          { label: 'Inactivas',         value: byStatus.inactive,   icon: 'fa-circle-xmark',  color: '#f04438' },
          { label: 'Vencen en 30 días', value: nVencen,               icon: 'fa-triangle-exclamation', color: '#ff6b35' },
        ].map(k => (
          <div key={k.label} className={`${styles.tarjeta} ${styles.kpi}`}>
            <div className={styles.kpiIcono} style={{ '--tono': k.color, '--fondo': k.color + '22' }}>
              <i className={`fas ${k.icon} ${styles.kpiIconoGlifo}`} />
            </div>
            <div className={styles.kpiValor}>{k.value}</div>
            <div className={styles.kpiEtiqueta}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Fila: donut + barras por unidades */}
      <div className={styles.filaGraficos}>

        {/* Donut membresías */}
        <div className={styles.tarjeta}>
          <h3 className={styles.tituloTarjeta}>
            <i className={`fas fa-chart-pie ${styles.iconoTitulo}`} />
            Estado de membresías
          </h3>
          <div className={styles.donaFila}>
            <DonutChart segments={[
              { value: byStatus.active,   color: '#12b76a' },
              { value: byStatus.trial,    color: '#f79009' },
              { value: byStatus.inactive, color: '#f04438' },
            ]} />
            <div className={styles.leyendaColumna}>
              {Object.entries(byStatus).map(([k, v]) => (
                <div key={k} className={styles.leyendaItem}>
                  <span className={styles.puntoLeyenda} style={{ '--tono': STATUS_COLOR[k] }} />
                  <span className={styles.leyendaNombre}>{STATUS_LABEL[k]}</span>
                  <span className={styles.leyendaValor}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Barras: propiedades con más unidades */}
        <div className={styles.tarjeta}>
          <h3 className={styles.tituloTarjeta}>
            <i className={`fas fa-chart-bar ${styles.iconoTitulo}`} />
            Top 10 propiedades por unidades
          </h3>
          {topByUnits.length === 0
            ? <p className={styles.sinDatos}>Sin datos</p>
            : <>
                <BarChart
                  data={topByUnits.map(p => ({ label: p.name, value: p.unitCount, status: p.realStatus }))}
                  colorFn={d => STATUS_COLOR[d.status] || 'var(--accent)'}
                />
                {/* Leyenda */}
                <div className={styles.leyendaBarras}>
                  {[['active','Activa'],['trial','Trial'],['inactive','Inactiva']].map(([k, label]) => (
                    <div key={k} className={styles.leyendaBarra}>
                      <span className={styles.cuadroLeyenda} style={{ '--tono': STATUS_COLOR[k] }} />
                      <span className={styles.leyendaBarraTexto}>{label}</span>
                    </div>
                  ))}
                </div>
              </>
          }
        </div>
      </div>

      {/* Tabla de membresías */}
      <div className={styles.tarjeta}>
        <h3 className={styles.tituloTarjeta}>
          <i className={`fas fa-table ${styles.iconoTitulo}`} />
          Resumen de membresías
        </h3>
        <p className={styles.notaTabla}>
          Las {props.length} propiedades más recientes de {total}.
        </p>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Propiedad</th>
                <th>Dirección</th>
                <th>Membresía</th>
                <th>Unidades</th>
                <th>Fecha adquisición</th>
                <th>Vencimiento</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {props.map(p => {
                const realStatus = p.estado_real;
                const expires  = p.subscription_expires_at ? new Date(p.subscription_expires_at) : null;
                const daysLeft = expires ? Math.ceil((expires - new Date()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <tr key={p.id}>
                    <td><strong>{p.name}</strong></td>
                    <td className={styles.celdaDireccion}>{p.address || '—'}</td>
                    <td>
                      <span className={styles.chipEstado}
                        style={{ '--fondo': STATUS_BG[realStatus] || '#ccc', '--tono': STATUS_COLOR[realStatus] || '#666' }}>
                        {STATUS_LABEL[realStatus] || '—'}
                      </span>
                    </td>
                    <td className={styles.celdaCentro}>{p.unidades}</td>
                    <td className={styles.celdaTexto13}>
                      {p.created_at ? new Date(p.created_at).toLocaleDateString('es-CO') : '—'}
                    </td>
                    <td className={styles.celdaTexto13}>
                      {expires ? (
                        <span className={daysLeft !== null && daysLeft <= 30 ? styles.vencePronto : undefined}>
                          {expires.toLocaleDateString('es-CO')}
                          {daysLeft !== null && daysLeft <= 30 && daysLeft >= 0 && (
                            <span className={styles.diasRestantes}>
                              ({daysLeft}d)
                            </span>
                          )}
                        </span>
                      ) : <span className={styles.sinFecha}>Sin fecha</span>}
                    </td>
                    <td>
                      {daysLeft !== null && daysLeft < 0
                        ? <span className={styles.estadoVencida}>⚠ Vencida</span>
                        : daysLeft !== null && daysLeft <= 30
                        ? <span className={styles.estadoPorVencer}>⏰ Por vencer</span>
                        : <span className={styles.estadoOk}>✓ OK</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > props.length && (
          <div className={styles.pieEnlace}>
            <Link to="/admin/membresias" className={styles.enlaceVer}>
              Ver todas ({total}) <i className={`fas fa-arrow-right ${styles.flechaChica}`} />
            </Link>
          </div>
        )}
      </div>

      {/* Alerta propiedades próximas a vencer */}
      {expiringSoon.length > 0 && (
        <div className={styles.tarjetaAlerta}>
          <h3 className={styles.tituloAlerta}>
            <i className={`fas fa-triangle-exclamation ${styles.iconoAlerta}`} />
            Membresías próximas a vencer (30 días)
          </h3>
          <p className={styles.notaProximas}>
            Mostrando las {expiringSoon.length} que vencen primero{nVencen > expiringSoon.length ? ` de ${nVencen}` : ''}.
          </p>
          <div className={styles.listaProximas}>
            {expiringSoon.map(p => {
              const days = Math.ceil((new Date(p.subscription_expires_at) - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={p.id} className={styles.filaProxima}>
                  <i className={`fas fa-building ${styles.iconoFila}`} />
                  <span className={styles.nombreFila}>{p.name}</span>
                  <span className={styles.direccionFila}>{p.address}</span>
                  <span className={`${styles.diasFila} ${days <= 7 ? styles.diasUrgente : ''}`}>
                    Vence en {days} día{days !== 1 ? 's' : ''}
                  </span>
                </div>
              );
            })}
          </div>
          {nVencen > expiringSoon.length && (
            <div className={styles.pieEnlace}>
              <Link to="/admin/membresias?filtro=por-vencer" className={styles.enlaceVer}>
                Ver todas ({nVencen}) <i className={`fas fa-arrow-right ${styles.flechaChica}`} />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard Admin ──────────────────────────────────────────────────────────
const adminCards = [
  { label: 'Mi propiedad', icon: 'fa-building',        color: '#4ecdc4', to: '/admin/propiedades' },
  { label: 'Torres',       icon: 'fa-layer-group',     color: '#6c63ff', to: '/admin/torres' },
  { label: 'Unidades',     icon: 'fa-door-open',       color: '#ff6b35', to: '/admin/unidades' },
  { label: 'Propietarios', icon: 'fa-users',            color: '#273FF5', to: '/admin/propietarios-import' },
  { label: 'Asambleas',   icon: 'fa-gavel',            color: '#ffd93d', to: '/admin/asambleas' },
  { label: 'Preguntas',   icon: 'fa-circle-question',  color: '#6bcb77', to: '/admin/preguntas' },
  { label: 'Asistentes',  icon: 'fa-clipboard-list',   color: '#a78bfa', to: '/admin/asistentes' },
];

// Estados de membresía por propiedad — a diferencia del resto de la app (que
// oculta por completo una propiedad vencida), este resumen SÍ debe mostrar
// todas las propiedades del admin para que sepa a cuál llamar a reactivar.
const MEMBERSHIP_LABEL = {
  active:        'Activa',
  expiring_soon: 'Por vencer',
  grace_period:  'Vencida (en gracia)',
  expired:       'Vencida',
};
const MEMBERSHIP_COLOR = {
  active: '#12b76a', expiring_soon: '#f79009', grace_period: '#f79009', expired: '#f04438',
};
const MEMBERSHIP_BG = {
  active: '#12b76a22', expiring_soon: '#f7900922', grace_period: '#f7900922', expired: '#f0443822',
};
const MEMBERSHIP_ICON = {
  active: 'fa-circle-check', expiring_soon: 'fa-clock', grace_period: 'fa-triangle-exclamation', expired: 'fa-circle-xmark',
};
// Tipo de plan (informativo)
const PLAN_LABEL = {
  standard:  'Estándar',
  premium:   'Premium',
  asamblea:  'Asamblea',
  logistica: 'Asamblea + Logística',
};

function MembershipSummary() {
  const [props,   setProps]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    propertyService.dashboardSummary()
      .then(r => { setProps(r.data.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (props.length === 0) return null;

  const anyNeedsAttention = props.some(p => p.membership_label !== 'active');

  return (
    <div className={`card ${styles.tarjetaResumen}`}>
      <h3 className={styles.tituloResumen}>
        <i className={`fas fa-id-card ${styles.iconoTitulo}`} />
        Estado de tus membresías
      </h3>
      <p className={styles.notaResumen}>
        {anyNeedsAttention
          ? 'Una propiedad con membresía vencida no muestra información hasta reactivarla.'
          : 'Todas tus propiedades tienen membresía activa.'}
      </p>
      <div className={styles.listaResumen}>
        {props.map(p => {
          const expires  = p.subscription_expires_at ? new Date(p.subscription_expires_at) : null;
          const daysLeft = expires ? Math.ceil((expires - new Date()) / (1000 * 60 * 60 * 24)) : null;
          const label = p.membership_label;
          return (
            <div key={p.id} className={styles.filaMembresia}
              style={{ '--tono': MEMBERSHIP_COLOR[label], '--fondo': MEMBERSHIP_BG[label] }}>
              <div className={styles.cajaIcono}>
                <i className={`fas ${MEMBERSHIP_ICON[label]} ${styles.cajaIconoGlifo}`} />
              </div>
              <div className={styles.bloqueNombre}>
                <div className={styles.nombrePropiedad}>{p.name}</div>
                <div className={styles.subLinea}>
                  <span className={styles.chipPlan}>
                    Plan {PLAN_LABEL[p.subscription_type] || p.subscription_type || 'no asignado'}
                  </span>
                  <span>{[p.city, p.address].filter(Boolean).join(' · ') || '—'}</span>
                </div>
              </div>
              <div className={styles.bloqueDerecha}>
                <span className={styles.chipEstado}>
                  {MEMBERSHIP_LABEL[label]}
                </span>
                {expires && (
                  <div className={styles.venceEn}>
                    {daysLeft >= 0 ? `Vence en ${daysLeft} día${daysLeft !== 1 ? 's' : ''}` : `Venció hace ${Math.abs(daysLeft)} día${Math.abs(daysLeft) !== 1 ? 's' : ''}`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {anyNeedsAttention && (
        <div className={styles.avisoSoporte}>
          <i className={`fas fa-phone ${styles.avisoSoporteIcono}`} />
          Llama a soporte de EVENTIFY para reactivar la membresía de las propiedades marcadas.
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ user }) {
  return (
    <div>
      <MembershipSummary />
      <div className={styles.atajos}>
        {adminCards.map(card => (
          <Link to={card.to} key={card.to} className={styles.enlaceAtajo}>
            <div className={`card ${styles.tarjetaAtajo}`}>
              <div className={styles.atajoIcono} style={{ '--tono': card.color, '--fondo': card.color + '22' }}>
                <i className={`fas ${card.icon} ${styles.atajoIconoGlifo}`} />
              </div>
              <div className={styles.atajoTitulo}>{card.label}</div>
              <div className={`text-muted ${styles.atajoSub}`}>Ver todos <i className={`fas fa-arrow-right ${styles.flechaChica}`} /></div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// UserDashboard → componente separado (UserDashboard.jsx)

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, isSuperAdmin, isAdmin, isAssemblyAdmin } = useAuth();

  return (
    <div>
      <div className="page-header">
        <div className={styles.encabezadoTexto}>
          <h1 className={styles.saludo}>Bienvenido, {user?.name?.split(' ')[0]} 👋</h1>
          <p>
            {isSuperAdmin    ? 'Panel de control — Super Administrador'
              : isAdmin         ? 'Panel de gestión de Propiedad Horizontal'
              : isAssemblyAdmin ? 'Panel de Administrador de Asamblea'
              : user?.is_temporary_rep ? 'Panel del representante — EVENTIFY'
              : 'Panel del propietario — EVENTIFY'}
          </p>
        </div>
      </div>

      {isSuperAdmin    && <SuperAdminDashboard />}
      {!isSuperAdmin && isAdmin && <AdminDashboard user={user} />}
      {isAssemblyAdmin && <AssemblyAdminDashboard />}
      {!isAdmin && !isAssemblyAdmin && <UserDashboard />}
    </div>
  );
}